import { Button, Form, Drawer, DrawerProps, Input, Spin, Pagination, message } from 'antd';
import { css } from '@emotion/css';
import { Formik } from 'formik';
import FormField from 'components/FormField';
import type { PhysicalProduct } from './type';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import FilterBar from './FilterBar';
import Operate from './Operate';
import {
  SpuSelectContext,
  OperateValueProvider,
  FilterBarValueProvider,
  FilterBarValueContext,
  OperateValueContext,
} from './context';
import StatusBar from './StatusBar';
import GridView from './GridView';
import Empty from '../components/Empty';
import introduceBg from '@knowledge/assets/introduce-bg.png';
import { Title } from './Layout';
import { splitSku, useSelectControl } from '@knowledge/common/helpers/sku';
import { searchInItemScope } from '@knowledge/common/helpers/search';
import { ItemSelectV2 } from '@knowledge/common/components/ItemSelectV2';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import { useMount, useRequest } from 'ahooks';
import useSmartField from 'hooks/useSmartField';
import { postProduct, putProduct } from './service';
import { microTrans } from './microsoftTranslate';
import { filter, find } from 'lodash-es';
import { useTranslate } from '@tolgee/react';

export interface EditProductProps extends DrawerProps {
  productData?: PhysicalProduct;
  spuClassId?: number;
  listData?: readonly PhysicalProduct[];
}

type FormValues = {
  name: string;
  spus: ItemSelectV2.Item[];
};

const _defaultValue: FormValues = {
  name: '',
  spus: [] as ItemSelectV2.Item[],
};

export const EditProduct = (props: EditProductProps) => {
  return (
    <FilterBarValueProvider>
      <OperateValueProvider>
        <IEditProduct {...props} />
      </OperateValueProvider>
    </FilterBarValueProvider>
  );
};
const IEditProduct = (props: EditProductProps) => {
  const { productData, listData, spuClassId, ...restProps } = props;
  const { t } = useTranslate();
  const [initialValues, setInitialValues] = useState<FormValues>(_defaultValue);
  const mode = productData?.id ? 'edit' : 'create';
  const [loading, setLoading] = useState(false);
  const { sortBy } = useContext(OperateValueContext);
  const [visible, setVisible] = useState(false);
  const orderItems = useCallback(
    (items: ItemSelectV2.Item[] = [], callback: (items: ItemSelectV2.Item[]) => void) => {
      setLoading(true);
      getItemsByInternalIds(
        items.map((item) => {
          return {
            itemInternalId: item.internalItemId,
            skus: item.skuInfo,
          };
        }) || [],
        sortBy!,
      )
        .then((res) => {
          callback(res);
        })
        .catch(() => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
        })
        .finally(() => setLoading(false));
    },
    [sortBy, t],
  );

  useMount(() => {
    setLoading(true);
    getItemsByInternalIds(productData?.spus || [], sortBy!)
      .then((res) => {
        setInitialValues((prevState) => {
          return {
            ...prevState,
            spus: res,
            name: productData?.name || '',
          };
        });
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      })
      .finally(() => setLoading(false));
  });
  useEffect(() => {
    if (restProps.visible === false) {
      setInitialValues(_defaultValue);
    }
  }, [restProps]);

  const handleAdd = () => setVisible(true);
  const { run: post, loading: postLoading } = useRequest((data) => postProduct(data), {
    manual: true,
    onSuccess: () => {
      message.success(t('operation.saveSucceed'));
      restProps.onClose?.(null!);
    },
    onError: () => {
      message.error(t('operation.saveFailed'));
    },
  });
  const { run: put, loading: putLoading } = useRequest((data) => putProduct(data), {
    manual: true,
    onSuccess: () => {
      message.success(t('operation.saveSucceed'));
      restProps.onClose?.(null!);
    },
    onError: () => {
      message.error(t('operation.saveFailed'));
    },
  });

  const mutateLoading = mode === 'create' ? postLoading : putLoading;

  return (
    <>
      <Formik<FormValues>
        enableReinitialize
        validateOnChange={false}
        initialValues={initialValues}
        onSubmit={(values) => {
          const data = {
            name: values.name,
            spus: values.spus.map((spu) => {
              return {
                itemInternalId: spu.internalItemId,
                skus: spu.skuInfo.map((sku) => {
                  return {
                    skuId: sku.skuId,
                    skuName: sku.skuName,
                    sellerSku: sku.sellerSku,
                  };
                }),
              };
            }),
          };
          microTrans
            .getTranslate([values.name])
            .then((res) => {
              if (mode === 'create') {
                post({
                  spuClassId: spuClassId!,
                  multiLang: JSON.stringify({
                    'en-US': res.get(values.name),
                  }),
                  ...data,
                });
              } else {
                put({
                  physicalProductId: productData!.id,
                  multiLang: JSON.stringify({
                    'en-US': res.get(values.name),
                  }),
                  ...data,
                });
              }
            })
            .catch(() => {
              if (mode === 'create') {
                post({
                  spuClassId: spuClassId!,
                  ...data,
                });
              } else {
                put({
                  physicalProductId: productData!.id,
                  ...data,
                });
              }
            });
        }}
      >
        {({ values, dirty, submitForm }) => {
          const { spus, name } = values;

          return (
            <Drawer
              title={mode === 'create' ? t('bs_6') : t('bs_7')}
              width={1200}
              className={css`
                .ant-drawer-body {
                  padding: 16px !important;
                  background: #f0f2f5;
                }
              `}
              footer={
                <div className="flex justify-center">
                  <Button
                    loading={mutateLoading}
                    onClick={() => submitForm()}
                    className="mr-[16px]"
                    type="primary"
                    disabled={!name || !dirty}
                  >
                    {t('api.common.action.save')}
                  </Button>
                  <Button onClick={() => restProps.onClose?.(null!)}>
                    {t('antd.modal.cancel')}
                  </Button>
                </div>
              }
              {...restProps}
            >
              <Form className="h-full" validateTrigger={['onBlur', 'onSubmit']}>
                <div className="h-full flex flex-col">
                  <div className="bg-[#fff] p-[16px] flex-shrink-0">
                    <Title title={t('department.基础信息')} />
                    <div className="mt-[12px] flex items-start">
                      <FormField
                        name="name"
                        label={t('bs_9')}
                        className="!mb-0"
                        validateTrigger={['onBlur', 'onSubmit']}
                        validate={(value) => {
                          const diff = filter(listData, (e) => e.id !== productData?.id);
                          if (find(diff, (e) => e.name === value)) return t('bs_83');
                          return undefined;
                        }}
                      >
                        {({ field, helpers }) => {
                          return (
                            <Input
                              value={field.value}
                              onChange={(e) => helpers.setValue(e.target.value)}
                              maxLength={200}
                              showCount
                              onBlur={field.onBlur}
                              className="!min-w-[340px]"
                              placeholder={t('trade.请输入')}
                            />
                          );
                        }}
                      </FormField>
                    </div>
                  </div>

                  <FormField name="spus" noStyle>
                    {({ field, helpers }) => {
                      return (
                        <ItemSelectV2
                          visible={visible}
                          onCancel={() => setVisible(false)}
                          value={field.value}
                          onChange={(value) => {
                            orderItems(value, (res) => {
                              helpers.setValue(res);
                            });
                          }}
                          storeType="ALL"
                          storeScopes={[]}
                        />
                      );
                    }}
                  </FormField>
                  <div className="basis-auto flex-grow bg-[#fff] px-[16px] pb-[8px] pt-[8px] mt-[16px] flex flex-col overflow-hidden">
                    <div className="flex-shrink-0 flex justify-between items-center">
                      <Title title={t('todo.关联商品')} />
                      {spus.length > 0 && (
                        <Button onClick={handleAdd} type="primary">
                          + {t('aide.添加商品')}
                        </Button>
                      )}
                    </div>
                    <div className="basis-auto flex-grow bg-[#fff] overflow-hidden">
                      {loading ? (
                        <div className="flex items-center justify-center w-full h-full">
                          <Spin className="m-auto" />
                        </div>
                      ) : spus.length > 0 ? (
                        <EditArea spus={spus} />
                      ) : (
                        <div className="h-full items-center justify-center flex flex-col">
                          <img width={120} src={introduceBg} alt="" className="mb-[14px]" />
                          <Button onClick={handleAdd} type="primary">
                            + {t('aide.添加商品')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            </Drawer>
          );
        }}
      </Formik>
    </>
  );
};

const EditArea = (props: { spus: ItemSelectV2.Item[] }) => {
  const { spus } = props;
  const { helpers } = useSmartField<FormValues['spus']>('spus');
  const [
    spuSelectMap,
    selectedSkuLen,
    { toggleSku, toggleSpu, toggleBatch, getBatchStatus, cancelSelect },
  ] = useSelectControl(spus);
  const [filterSpus, setFilterSpus] = useState<ItemSelectV2.Item[]>(spus);
  const { t } = useTranslate();
  const { storeScopes, keyword, keywordType, name } = useContext(FilterBarValueContext);
  useEffect(() => {
    searchInItemScope(spus, {
      storeScopes,
      keyword,
      keywordType,
      name,
    }).then((res) => {
      setFilterSpus(res);
    });
  }, [spus, storeScopes, keyword, keywordType, name]);
  const [pageInfo, setPageInfo] = useState<{ pageSize: number; pageNo: number }>({
    pageSize: 10,
    pageNo: 1,
  });
  const currentPageSpus = useMemo<ItemSelectV2.Item[]>(() => {
    return filterSpus.slice(
      (pageInfo.pageNo - 1) * pageInfo.pageSize,
      pageInfo.pageNo * pageInfo.pageSize,
    );
  }, [filterSpus, pageInfo]);
  const currentPageSpuIds = useMemo(() => {
    return currentPageSpus.map((spu) => spu.internalItemId);
  }, [currentPageSpus]);
  const handleRemove = useCallback(() => {
    cancelSelect();
    helpers.setValue(splitSku(spus, spuSelectMap) as ItemSelectV2.Item[]);
  }, [spus, spuSelectMap, cancelSelect, helpers]);

  return (
    <div className="h-full flex flex-col">
      {/*操作区*/}
      <div className="basis-[92px] flex-shrink-0 pt-[12px]">
        <div className="flex justify-between items-center">
          <FilterBar />
          <Operate />
        </div>
        <div className="py-[9px]">
          <SpuSelectContext.Provider
            value={{
              spuSelectMap,
              getBatchStatus,
              toggleBatch,
              cancelSelect,
            }}
          >
            <StatusBar
              empty={filterSpus.length === 0}
              selectedSkuLen={selectedSkuLen}
              currentPageSpuIds={currentPageSpuIds}
              onRemove={handleRemove}
            />
          </SpuSelectContext.Provider>
        </div>
      </div>

      {/*列表*/}
      <div className="basis-auto flex-grow overflow-auto">
        {filterSpus.length > 0 ? (
          <SpuSelectContext.Provider
            value={{
              spuSelectMap,
              toggleSku,
              toggleSpu,
            }}
          >
            <GridView spus={currentPageSpus} />
          </SpuSelectContext.Provider>
        ) : (
          <div className="h-full flex items-center justify-center">
            <Empty description={t('storeSelector.暂无数据')} />
          </div>
        )}
      </div>
      {/*分页*/}
      <div className="basis-[45px] flex-shrink-0 flex items-center justify-end">
        <Pagination
          total={filterSpus.length}
          showSizeChanger
          size="small"
          current={pageInfo.pageNo}
          pageSize={pageInfo.pageSize}
          pageSizeOptions={[10, 20, 50]}
          onChange={(pageNo, pageSize) => {
            setPageInfo({
              pageNo,
              pageSize: pageSize || 10,
            });
          }}
        />
      </div>
    </div>
  );
};
